import { createApi } from '@reduxjs/toolkit/query/react';

import { SoloProductQuery } from '@/helpers/utils/qs';
import { ResponseSocialLinks, TopHeaderTextRepsonse } from '@/types/Common';
import { ResponseCartAbandonmentModal, ResponseInterstitialModal } from '@/types/InterstitialModal';
import { CategoryWithProducts, ProductStrapi } from '@/types/Product';
import { StrapiRealProduct } from '@/types/Strapi';

import { singleProductAdapter } from '../adapters';
import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

export const productApi = createApi({
  reducerPath: 'productApi',
  keepUnusedDataFor: 300,
  // baseQuery: fetchBaseQuery({
  //   baseUrl: STRAPI_SERVER_URL,
  //   cache: 'no-store',
  // }),

  baseQuery: generateBaseQuseryWithReAuth({ useStrapiServer: true, basePath: '', cache: 'no-store' }),
  tagTypes: ['SingleProduct', 'Product', 'FilteredProducts'],
  endpoints: builder => ({
    getSingleProduct: builder.query<{ product: ProductStrapi }, string | number>({
      query: q => ({
        url: `/api/products/${q}?${SoloProductQuery}`,
        next: { tags: ['SingleProduct'] },
      }),
      transformResponse: (response: { data: StrapiRealProduct }, meta, arg) => singleProductAdapter(response.data),
      providesTags: ['SingleProduct'],
    }),
    getFilteredProducts: builder.query<{ products: ProductStrapi[] }, string | string[] | undefined>({
      query: q => ({ url: `/search?query=${q}`, next: { tags: ['FilteredProducts'] } }),
      transformResponse: (response: { data: { products: ProductStrapi[] } }, meta, arg) => response.data,
      providesTags: ['FilteredProducts'],
    }),
    getAllCategoriesWithProducts: builder.query<CategoryWithProducts[], void>({
      query: () => ({
        url: `/api/products/all-categories-products-recommendations`,
        next: { tags: ['Product'] },
      }),
      transformResponse: (response: { success: boolean; data: CategoryWithProducts[] }) => response.data,
      providesTags: ['Product'],
    }),
    getAllProductsFromItemCodes: builder.mutation<ProductStrapi[], { itemCodes: string[] }>({
      query: ({ itemCodes }) => ({
        url: `/api/products/by-item-codes`,
        method: 'POST',
        body: { itemCodes },
      }),
      transformResponse: (response: { success: boolean; data: ProductStrapi[] }) => response.data,
    }),
    getTopHeaderContentFromStrapi: builder.query<any, void>({
      query: () => ({
        url: '/pawtree/rs-top-strip',
      }),
      transformResponse: (response: { success: boolean; data: TopHeaderTextRepsonse }) => response.data,
    }),
    getSocialLinkList: builder.query<ResponseSocialLinks, void>({
      query: () => ({
        url: '/pawtree/social-media-link',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ResponseSocialLinks }) => response.data,
    }),
    getInterstitialPopupDetails: builder.query<ResponseInterstitialModal, void>({
      query: () => ({
        url: '/pawtree/homepageCollectionPopup',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ResponseInterstitialModal }) => response.data,
    }),
    getCartAbandonmentPopupDetails: builder.query<ResponseCartAbandonmentModal, void>({
      query: () => ({
        url: '/pawtree/cartAbandonmentPopup',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ResponseCartAbandonmentModal }) => response.data,
    }),
  }),
});

export const {
  useGetAllCategoriesWithProductsQuery,
  useGetSingleProductQuery,
  useGetAllProductsFromItemCodesMutation,
  useGetTopHeaderContentFromStrapiQuery,
  useGetSocialLinkListQuery,
  useGetInterstitialPopupDetailsQuery,
  useGetCartAbandonmentPopupDetailsQuery,
} = productApi;
